import { Exercise as ComponentExercise, ExerciseType, GlossaryData, GlossaryTopic } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { BookOpen } from "lucide-react";
import { getExerciseProperty, parseExerciseData } from "./utils";

/**
 * Editor para exercícios do tipo Glossário Interativo
 */
export class GlossaryInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício de glossário
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || (exercise.type !== ExerciseType.GlossaryDisplay && !GlossaryInfo.SupportsExerciseType(exercise.type as unknown as string))) {
      return {
        title: 'Glossário Interativo',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      const parsedExercise = parseExerciseData(exercise);
      
      // Tenta obter o título prioritariamente dos dados internos (data.title)
      // Se não existir, tenta da raiz (exercise.title)
      let title = 'Sem título';
      const data = parsedExercise.data as any;
      
      if (data && data.title) {
        title = data.title;
      } else if (parsedExercise.title) {
        title = parsedExercise.title;
      }
      
      // Calcular termsCount: somar termos de todos os tópicos (formato novo) ou usar terms diretamente (formato antigo)
      let termsCount = 0;
      if (data?.topics && Array.isArray(data.topics)) {
        // Formato novo: somar termos de todos os tópicos
        termsCount = data.topics.reduce((total: number, topic: GlossaryTopic) => {
          return total + (Array.isArray(topic.terms) ? topic.terms.length : 0);
        }, 0);
      } else if (data?.terms && Array.isArray(data.terms)) {
        // Formato antigo: usar terms diretamente
        termsCount = data.terms.length;
      }
      
      // Seguir o padrão do FormattedTextEditor: colocar o título do usuário em subtitle
      // para que apareça como título principal no componente ExerciseCard
      const result = {
        title: "Glossário Interativo", // Mantido para compatibilidade com outros componentes
        subtitle: title.length > 60 ? `${title.substring(0, 60)}...` : title || 'Sem título',
        description: `${termsCount} ${termsCount === 1 ? 'termo' : 'termos'}`,
      };
      console.log("GlossaryInfo.GetCardInfo result:", result);
      return result;
    } catch (error) {
      console.error("Erro ao processar dados do exercício de glossário:", error, exercise);
      return {
        title: 'Glossário Interativo',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Retorna o título do tipo de exercício de glossário
   */
  static getTitle(): string {
    return "Glossário Interativo";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício de glossário
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      const parsedExercise = parseExerciseData(exercise);
      const data = parsedExercise.data as any;
      
      // Calcular termsCount: somar termos de todos os tópicos (formato novo) ou usar terms diretamente (formato antigo)
      let termsCount = 0;
      if (data?.topics && Array.isArray(data.topics)) {
        // Formato novo: somar termos de todos os tópicos
        termsCount = data.topics.reduce((total: number, topic: GlossaryTopic) => {
          return total + (Array.isArray(topic.terms) ? topic.terms.length : 0);
        }, 0);
      } else if (data?.terms && Array.isArray(data.terms)) {
        // Formato antigo: usar terms diretamente
        termsCount = data.terms.length;
      }
      
      return termsCount > 0 ? `${termsCount} ${termsCount === 1 ? 'termo' : 'termos'}` : "Sem detalhes";
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error, exercise);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para exercícios de glossário
   */
  static getIcon() {
    return BookOpen;
  }
  
  /**
   * Verifica se o exercício é do tipo glossário
   */
  static SupportsExerciseType(type: string): boolean {
    return type === ExerciseType.GlossaryDisplay || type === "glossary_display";
  }
}

